/*
Simple Rich Text is for rich text renders in smaller components around the website.
Specifically, this was made for the blog category page's "calloutText" area. Its one caller is
`components/Blog/Category.tsx`, which renders `calloutText` with type "secondary" in both its
desktop and mobile layouts.

Node types handled: document, paragraph, heading-1 (a plain <h1>), heading-2, heading-3,
unordered-list, ordered-list, list-item, hyperlink, and text with the bold, italic and underline
marks. Any other node type returns an empty fragment, so its content (including the text of an
entry-hyperlink or asset-hyperlink) is dropped. The code mark, and any unrecognised mark, renders
as plain text.
*/
import React from "react";
import { ReactNode, Key } from "react";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import { css } from "@emotion/react";
import { RichTextNode, Bold, Italic, Underline, Text } from "./";

/**
 * Renders one node of a resolved Contentful rich-text tree (start with the `document` node) and,
 * recursively, its children. `type` is the design-system colour context passed to the paragraph
 * and heading-2/3 `Typography` and to the hyperlink `Link`; the mark wrappers (`Bold`, `Italic`,
 * `Underline`, `Text`) and the `<h1>`, `<ul>`, `<ol>` and `<li>` elements don't take it. `key` is
 * the child index the parent supplies.
 *
 * Returns `null` for a missing node or one without a string `nodeType`. Every hyperlink opens in
 * a new tab. For the article body, with embedded entries and assets, use `./Blog` →
 * `renderBlogText`.
 */
export const renderSimpleText = (
	node: RichTextNode,
	type: "primary" | "secondary" | "tertiary" = "primary",
	key?: Key
): React.ReactNode => {
	if (!node || typeof node.nodeType !== "string") return null;

	switch (node.nodeType) {
		case "text": {
			const value = node.value ?? "";
			if (!node.marks?.length) return value;

			return node.marks.reduce<ReactNode>((acc, mark) => {
				switch (mark.type) {
					case "bold":
						return <Bold>{acc}</Bold>;
					case "italic":
						return <Italic>{acc}</Italic>;
					case "underline":
						return <Underline>{acc}</Underline>;
					case "code": //unsupported
						return <Text>{acc}</Text>;
					default:
						return <Text>{acc}</Text>;
				}
			}, value);
		}
		case "paragraph":
			return (
				<Typography
					key={key}
					variant="p"
					css={css`
						margin-bottom: 16px;
						margin-top: 0px;
					`}
					type={type}
				>
					{node.content?.map((n, i) => renderSimpleText(n, type, i))}
				</Typography>
			);
		case "heading-1":
			return <h1 key={key}>{node.content?.map((n, i) => renderSimpleText(n, type, i))}</h1>;
		case "heading-2":
			return (
				<Typography
					key={key}
					variant="h2"
					type={type}
					css={css`
						margin-bottom: 20px;
					`}
				>
					{node.content?.map((n, i) => renderSimpleText(n, type, i))}
				</Typography>
			);
		case "heading-3":
			return (
				<Typography
					key={key}
					variant="h3"
					type={type}
					css={css`
						margin-bottom: 16px;
					`}
				>
					{node.content?.map((n, i) => renderSimpleText(n, type, i))}
				</Typography>
			);
		case "unordered-list":
			return <ul key={key}>{node.content?.map((n, i) => renderSimpleText(n, type, i))}</ul>;
		case "ordered-list":
			return <ol key={key}>{node.content?.map((n, i) => renderSimpleText(n, type, i))}</ol>;
		case "list-item":
			return <li key={key}>{node.content?.map((n, i) => renderSimpleText(n, type, i))}</li>;
		case "hyperlink": {
			return (
				<Link type={type} to={(node.data?.uri as string) ?? "#"} target="_blank">
					{node.content?.map((n, i) => renderSimpleText(n, type, i))}
				</Link>
			);
		}
		case "document":
			return <>{node.content?.map((n, i) => renderSimpleText(n, type, i))}</>;
		default:
			return <></>;
	}
};
